<template>
    <CoreBorderWrapper :condensed="true" class="text-bold">
        {{ $t('§eq_type.annual_usage') }}
    </CoreBorderWrapper>

    <CoreTwoColumnsRow>
        <template #left>
            <CoreBorderWrapper
                :condensed="true"
                class="flex-grow-1 flex-shrink-1 flex-basis-55 flex space-between"
            >
                <div class="flex-1">
                    {{ $t('§ui.placeholder_select_expected_utilization_unit') }}
                    {{ isFieldRequired('billing_units') }}
                </div>
                <CoreIcon
                    name="information-outline"
                    size="18"
                    class="ml-auto"
                    fillColor="var(--color-secondary-200)"
                    v-tooltip="{ value: $t('§eq_type.billing_units_tooltip') }"
                />
            </CoreBorderWrapper>
        </template>
        <template #right>
            <CoreMultiSelect
                :validator="v$.billing_units"
                v-model="unitTypeStore.unitTypeData.billing_units"
                class="flex-grow-1 flex-shrink-1 flex-basis-45"
                :options="billingUnitOptionsPlural(optionsStore.billingUnitOptions)"
                :filter="false"
                :placeholder="$t('§ui.placeholder_select_expected_utilization_unit')"
            />
        </template>
    </CoreTwoColumnsRow>

    <CoreTwoColumnsRow>
        <template #left>
            <CoreBorderWrapper
                :condensed="true"
                class="flex-grow-1 flex-shrink-1 flex-basis-55 flex space-between"
            >
                <div class="flex-1">
                    {{ $t('§eq_type.planned_usage') }}
                    {{ isFieldRequired('planned_usage_per_a') }}
                </div>
                <CoreIcon
                    name="information-outline"
                    size="18"
                    class="ml-auto"
                    fillColor="var(--color-secondary-200)"
                    v-tooltip="{ value: $t('§eq_type.planned_usage_tooltip') }"
                />
            </CoreBorderWrapper>
        </template>
        <template #right>
            <CoreNumberInput
                :validator="v$.planned_usage_per_a"
                v-model="unitTypeStore.unitTypeData.planned_usage_per_a"
                class="flex-grow-1 flex-shrink-1 flex-basis-45"
            />
        </template>
    </CoreTwoColumnsRow>

    <CoreTwoColumnsRow :class="{ 'mb-8': !isMaximumUsageShown }">
        <template #left>
            <CoreBorderWrapper
                :condensed="true"
                class="flex-grow-1 flex-shrink-1 flex-basis-55 flex space-between"
            >
                <div class="flex-1">
                    {{ $t('§eq_type.has_maximum_usage') }}
                </div>
                <CoreIcon
                    name="information-outline"
                    size="18"
                    class="ml-auto"
                    fillColor="var(--color-secondary-200)"
                    v-tooltip="{ value: $t('§eq_type.has_maximum_usage_tooltip') }"
                />
            </CoreBorderWrapper>
        </template>
        <template #right>
            <CoreBorderWrapper
                :condensed="true"
                :noBorder="true"
                class="flex-grow-1 flex-shrink-1 flex-basis-45 flex justify-center"
            >
                <CoreSelectButton
                    name="has_maximum_usage"
                    :options="hasMaximumUsageOptions"
                    :allowEmpty="false"
                    v-model="hasMaximumUsage"
                />
            </CoreBorderWrapper>
        </template>
    </CoreTwoColumnsRow>

    <CoreTwoColumnsRow v-if="isMaximumUsageShown" class="mb-8">
        <template #left>
            <CoreBorderWrapper
                :condensed="true"
                class="flex-grow-1 flex-shrink-1 flex-basis-55 flex space-between"
            >
                <div class="flex-1">
                    {{ $t('§eq_type.maximum_usage') }}
                    {{ isFieldRequired('maximum_usage_per_a') }}
                </div>
                <CoreIcon
                    name="information-outline"
                    size="18"
                    class="ml-auto"
                    fillColor="var(--color-secondary-200)"
                    v-tooltip="{ value: $t('§eq_type.maximum_usage_tooltip') }"
                />
            </CoreBorderWrapper>
        </template>
        <template #right>
            <CoreNumberInput
                :validator="v$.maximum_usage_per_a"
                v-model="unitTypeStore.unitTypeData.maximum_usage_per_a"
                class="flex-grow-1 flex-shrink-1 flex-basis-45"
            />
        </template>
    </CoreTwoColumnsRow>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'

import { useUnitTypeStore } from '@/stores/unitType'
import { useOptionsStore } from '@/stores/options'
import rules from '../validationRules.js'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreBorderWrapper from '@/components/core/CoreBorderWrapper.vue'
import CoreNumberInput from '@/components/core/CoreNumberInput.vue'
import CoreMultiSelect from '@/components/core/CoreMultiSelect.vue'
import CoreSelectButton from '@/components/core/CoreSelectButton.vue'
import CoreIcon from '@/components/core/CoreIcon.vue'

export default {
    name: 'InputsUtilizationInfo',

    components: {
        CoreTwoColumnsRow,
        CoreBorderWrapper,
        CoreNumberInput,
        CoreMultiSelect,
        CoreSelectButton,
        CoreIcon,
    },

    data() {
        return {
            v$: {},
            unitTypeStore: useUnitTypeStore(),
            optionsStore: useOptionsStore(),
            isMaximumUsageShown: false,
        }
    },

    computed: {
        hasMaximumUsageOptions() {
            return [
                { value: true, label: this.$t('§ui_button.yes') },
                { value: false, label: this.$t('§ui_button.no') },
            ]
        },

        hasMaximumUsage: {
            get() {
                return this.isMaximumUsageShown
            },
            set(value) {
                this.isMaximumUsageShown = value
                if (!value) {
                    this.unitTypeStore.unitTypeData.maximum_usage_per_a = null
                }
            },
        },

        isValid() {
            return (
                !this.v$.planned_usage_per_a?.$invalid &&
                !this.v$.maximum_usage_per_a?.$invalid &&
                !this.v$.billing_units?.$invalid
            )
        },
    },

    watch: {
        'unitTypeStore.unitTypeData.maximum_usage_per_a': {
            handler(value) {
                if (value != null && value !== '') {
                    this.isMaximumUsageShown = true
                }
            },
            immediate: true,
        },

        isValid: {
            handler(newValue) {
                this.unitTypeStore.isValidUtilizationInfo = newValue
            },
            immediate: true,
        },
    },

    mounted() {
        this.$nextTick(() => {
            this.v$ = useVuelidate(rules, this.unitTypeStore.unitTypeData)
        })
    },
}
</script>
